import React, { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import BrandMark from '../components/BrandMark';
import { useI18n } from '../i18n';
import LanguageSwitcher from '../components/LanguageSwitcher';
import ThemeToggle from '../components/ThemeToggle';
import { fmtDate, fmtDateTime, fmtMoney, statusTag } from '../lib/format';
import MessageButton from '../components/MessageButton';
import MediaStrip from '../components/MediaStrip';
import DocumentList from '../components/DocumentList';
import ProjectStatement from '../components/ProjectStatement';
import { timelineFor, updateText, hasNewUpdates } from '../lib/updates';
import { getPayLink } from '../lib/payLink';

// "2 photos", but "1 photo" — the count is right under the customer's own
// words, and "1 photos" reads like nobody looked at the screen.
const photoCount = (n, t) => t(n === 1 ? 'req.photoCountOne' : 'req.photoCount').replace('{n}', n);

// Off to the pay page for this job or project. A job that was invoiced but has
// nothing left to pay online is caught by the pay page itself, which says so.
async function goPay({ requestId, projectId, token, auth }) {
  try {
    window.location.href = await getPayLink({ requestId, projectId, token, auth });
  } catch (err) {
    alert(err.response?.data?.message || 'The pay page could not be opened. Please try again.');
  }
}

// The job's invoice still has something on it to pay online.
const invoiceOpen = (req) => req.invoiceStatus === 'pending' && (req.invoice?.amount || 0) > 0;

export function HomeownerDashboard() {
  const { currentScreen, currentUser, goTo, logout, homeownerTab, setHomeownerTab, requests, setActiveRequest, projects, setModal, authHeaders, openStatementInvoice } = useApp();
  const { t, optLabel } = useI18n();

  // Opening a work order: the card is the summary, this is everything on it.
  const openRequest = (req) => { setActiveRequest(req); goTo('screen-request-detail'); };

  return (
    <div className={`screen ${currentScreen === 'screen-homeowner-dashboard' ? 'active' : ''}`}>
      <nav className="nav">
        <BrandMark className="nav-brand" />
        <div className="nav-right" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: '#94a3b8', fontSize: 13 }}>{currentUser?.name}</span>
          <ThemeToggle /><LanguageSwitcher dark />
          {/* A client had no way to change their own password either. Forgot
              Password was the only route, and it needs an email to arrive. */}
          <button className="btn btn-ghost btn-sm" style={{ color: 'white', borderColor: '#334155' }}
            onClick={() => setModal('password')}>{t('acct.password')}</button>
          <button className="btn btn-ghost btn-sm" style={{ color: 'white', borderColor: '#334155' }} onClick={logout}>{t('nav.logout')}</button>
        </div>
      </nav>

      <div style={{ background: 'var(--white)', borderBottom: '1px solid var(--border)', padding: '0 20px', display: 'flex', gap: 0 }}>
        <button className={`tab-btn ${homeownerTab === 'requests' ? 'active' : ''}`} onClick={() => setHomeownerTab('requests')}>{t('ho.myRequests')}</button>
        {projects?.length > 0 && (
          <button className={`tab-btn ${homeownerTab === 'project' ? 'active' : ''}`} onClick={() => setHomeownerTab('project')}>{t('pj.tab')}</button>
        )}
        <button className={`tab-btn ${homeownerTab === 'new' ? 'active' : ''}`} onClick={() => { setHomeownerTab('new'); goTo('screen-request'); }}>{t('ho.newRequest')}</button>
      </div>

      {/* The project statement — the same sheet Julian fills in with them */}
      {homeownerTab === 'project' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {(projects || []).length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--muted)', lineHeight: 1.5 }}>{t('pj.noProjects')}</div>
          ) : projects.map(pr => (
            <div key={pr._id} style={{ marginBottom: 24 }}>
              <div className="qps-sheet-title">{pr.title}</div>
              {pr.address && <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12 }}>{pr.address}</div>}
              {/* The customer's own copy. Clicking the trade or the number
                  opens that bill, which is the question they ring up to ask:
                  what exactly am I paying for. Built from the data the server
                  already sent them, so it cannot show a cost that never left
                  the building. */}
              <ProjectStatement project={pr}
                onOpenInvoice={(line) => openStatementInvoice(pr, line)}
                onPay={(p) => goPay({ projectId: p._id, token: p.payToken, auth: authHeaders() })} />
            </div>
          ))}
        </div>
      )}

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: homeownerTab === 'project' ? 'none' : undefined }}>
        {requests.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', marginBottom: 8 }}>{t('ho.noRequests')}</div>
            <p style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 20 }}>{t('ho.noRequestsSub')}</p>
            <button className="btn btn-primary" onClick={() => goTo('screen-request')}>{t('req.title')}</button>
          </div>
        ) : (
          requests.map(req => (
            // The whole card opens the work order. The buttons inside it stop
            // the click themselves, so Messages and Review Estimate still do
            // their own thing rather than opening the job behind them.
            <div key={req._id} className="card card-tappable" style={{ marginBottom: 12 }}
              role="button" tabIndex={0}
              onClick={() => openRequest(req)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openRequest(req); } }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 3 }}>{optLabel(req.serviceType)}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>{req.serviceAddress}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {/* Something happened that they haven't looked at yet. Their
                      own submission never counts — see hasNewUpdates. */}
                  {hasNewUpdates(req) && <span className="tag tag-blue">{t('ho.newUpdate')}</span>}
                  {statusTag(req.status)}
                  <span aria-hidden style={{ color: 'var(--blue)', fontSize: 18, lineHeight: 1 }}>›</span>
                </div>
              </div>
              {/* Everything they asked for, in the words they asked for it in.
                  A customer who listed four jobs should be able to see all four
                  on their own request — that is the whole point of listing them
                  separately rather than typing a paragraph. */}
              {req.items?.length > 1 && (
                <div style={{ margin: '0 0 8px' }}>
                  {req.items.map((it, i) => (
                    <div key={i} style={{ fontSize: 13, color: 'var(--slate)', padding: '3px 0', display: 'flex', gap: 6 }}>
                      <span style={{ color: 'var(--muted)' }}>{i + 1}.</span>
                      <span>
                        {it.title || optLabel(it.serviceType)}
                        {(it.photos?.length > 0) && <span style={{ color: 'var(--muted)' }}> · {photoCount(it.photos.length, t)}</span>}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: req.estimateStatus === 'pending' ? 10 : 0 }}>{t('ho.submitted')} {fmtDate(req.createdAt)}</div>

              {req.estimateStatus === 'pending' && (
                <div className="alert alert-amber" style={{ marginTop: 8 }}>
                  <strong>{t('ho.estimateReady')}</strong> {t('ho.tapReview')}
                  <br />
                  <button className="btn btn-amber btn-sm" style={{ marginTop: 8 }} onClick={(e) => { e.stopPropagation(); setActiveRequest(req); goTo('screen-estimate-review'); }}>
                    {t('ho.reviewEstimate')}
                  </button>
                </div>
              )}
              {req.estimateStatus === 'approved' && req.estimate && (
                <div style={{ marginTop: 8, fontSize: 13, color: 'var(--green)', fontWeight: 600 }}>
                  {t('ho.estApproved')} {fmtMoney(req.estimate.amount)}
                </div>
              )}
              {req.invoiceStatus === 'pending' && (
                <div className="alert alert-amber" style={{ marginTop: 8 }}>
                  <strong>{t('ho.invoiceReceived')}</strong> {t('ho.invoiceBody').replace('{amount}', fmtMoney(req.invoice?.amount))}
                  {invoiceOpen(req) && (
                    <div>
                      <button className="btn btn-primary btn-sm" style={{ marginTop: 8 }}
                        onClick={(e) => { e.stopPropagation(); goPay({ requestId: req._id, token: req.payToken, auth: authHeaders() }); }}>
                        Pay {fmtMoney(req.invoice?.amount)} online
                      </button>
                    </div>
                  )}
                </div>
              )}
              {req.status === 'paid' && (
                <div className="alert" style={{ marginTop: 8, background: 'var(--green-light)', color: 'var(--green)' }}>
                  {t('ho.jobComplete')}
                </div>
              )}

              <div style={{ marginTop: 10 }}>
                <MessageButton request={req} />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// One work order, opened from the list.
//
// The card is a summary — trade, address, a line or two. This is everything
// that request actually is: every job on it in the customer's own words, the
// photos they sent for each one, when we are coming, and where the money is up
// to. Julian's ask, and the obvious one: a customer who submitted four jobs and
// a dozen photos should be able to look at them again without ringing anyone.
//
// Read-only by design. The two things a customer can DO about a job — approve
// an estimate, send a message — already have their own screens, and both are
// linked from here rather than rebuilt.
export function RequestDetail() {
  const {
    currentScreen, goTo, activeRequest: req, requests, markRequestSeen, unreadCounts, setPrintDoc,
    currentUser, account, handleApproveEstimate, addRequestDocuments, authHeaders,
    raiseAccountWorkOrder, isRaisingAccountWO,
  } = useApp();
  const { t, optLabel } = useI18n();

  // A property manager lands here too, from the Commercial Accounts portal.
  const isManager = currentUser?.role === 'manager';
  const homeScreen = isManager ? 'screen-account-dashboard' : 'screen-homeowner-dashboard';

  // Prefer the freshly loaded copy: the list reloads after an approval, and a
  // detail screen showing the state from before it would be its own bug report.
  const live = requests?.find(r => r._id === req?._id) || req;

  // Opening the work order is what marks it seen. The dot on the card clears
  // because they LOOKED at it, not because time passed.
  //
  // `seenOnOpen` freezes what was new at that moment, so the entries stay
  // highlighted while they read instead of un-marking themselves under them.
  const [seenOnOpen, setSeenOnOpen] = useState(null);
  const open = currentScreen === 'screen-request-detail';
  useEffect(() => {
    if (!open || !live?._id) return;
    setSeenOnOpen(live.homeownerSeenAt || null);
    markRequestSeen(live._id);
    // Only when the screen opens or the job changes — not on every re-render,
    // or reading it would post a stamp every few seconds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, live?._id]);

  const timeline = timelineFor(live);
  const unread = unreadCounts?.[live?._id] || 0;

  return (
    <div className={`screen ${currentScreen === 'screen-request-detail' ? 'active' : ''}`}>
      <nav className="nav-public">
        <BrandMark />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ThemeToggle /><LanguageSwitcher />
          {/* This screen is shared with the Commercial Accounts portal — a work
              order reads the same whoever raised it — so Back has to return to
              whichever portal you came from. */}
          <button className="btn btn-ghost btn-sm" onClick={() => goTo(homeScreen)}>{t('nav.back')}</button>
        </div>
      </nav>
      {live && (
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }} className="pb">
          <div className="sec-label">{t('ho.workOrder')}</div>
          <div className="section-title" style={{ marginBottom: 4 }}>{optLabel(live.serviceType)}</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 10 }}>{live.serviceAddress}{live.city ? `, ${live.city}` : ''}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
            {statusTag(live.status)}
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>{t('ho.submitted')} {fmtDate(live.createdAt)}</span>
          </div>

          {/* When we are coming. Never invented: with no appointment set it says
              we will confirm, because nothing here has looked at a calendar. */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, padding: '4px 0' }}>
              <span style={{ color: 'var(--muted)' }}>{t('ho.appointment')}</span>
              <span style={{ fontWeight: 600, color: live.appointmentAt ? 'var(--green)' : 'var(--muted)', textAlign: 'right' }}>
                {live.appointmentAt ? fmtDateTime(live.appointmentAt) : t('ho.noAppointment')}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, padding: '4px 0' }}>
              <span style={{ color: 'var(--muted)' }}>{t('req.urgency')}</span>
              <span style={{ textAlign: 'right' }}>{optLabel(live.urgency)}</span>
            </div>
            {live.bestTime && (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, padding: '4px 0' }}>
                <span style={{ color: 'var(--muted)' }}>{t('req.bestTime')}</span>
                <span style={{ textAlign: 'right' }}>{optLabel(live.bestTime)}</span>
              </div>
            )}
          </div>

          {/* What has happened, newest first. The answer to "has anything
              happened with my job?" without ringing anyone — and for a job that
              ran before any of this was recorded, rebuilt from the dates that
              were always on the record (lib/updates.js). */}
          <div className="sec-label">{t('ho.updates')}</div>
          <div className="card" style={{ marginBottom: 16 }}>
            {timeline.map((u, i) => {
              // Frozen at the moment the screen opened, so a line they are
              // reading doesn't stop being new underneath them.
              const fresh = u.kind !== 'submitted' && (!seenOnOpen || new Date(u.at) > new Date(seenOnOpen));
              return (
                <div key={i} style={{ display: 'flex', gap: 10, padding: '9px 0', borderBottom: i < timeline.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <span aria-hidden style={{ marginTop: 6, width: 7, height: 7, flexShrink: 0, borderRadius: 50, background: fresh ? 'var(--blue)' : 'var(--border)' }}></span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13.5, color: 'var(--navy)', lineHeight: 1.45 }}>
                      {updateText(u, t)}
                      {fresh && <span className="tag tag-blue" style={{ marginLeft: 6 }}>{t('ho.new')}</span>}
                    </div>
                    {u.note && u.kind === 'completed' && (
                      <div style={{ fontSize: 12.5, color: 'var(--slate)', marginTop: 3, whiteSpace: 'pre-wrap' }}>{u.note}</div>
                    )}
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{fmtDateTime(u.at)}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* The jobs. A request submitted before line items existed has none,
              so its description stands in — those customers still have a work
              order to look at. */}
          <div className="sec-label">{t('ho.theWork')}</div>
          {live.items?.length > 0 ? live.items.map((it, i) => (
            <div key={i} className="card" style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>
                  {live.items.length > 1 && <span style={{ color: 'var(--muted)', fontWeight: 400 }}>{i + 1}. </span>}
                  {it.title || optLabel(it.serviceType)}
                </div>
                {it.serviceType && <span className="tag tag-slate">{optLabel(it.serviceType)}</span>}
              </div>
              {it.description && it.description !== it.title && (
                <div style={{ fontSize: 13.5, color: 'var(--slate)', lineHeight: 1.5, marginTop: 6, whiteSpace: 'pre-wrap' }}>{it.description}</div>
              )}
              <MediaStrip urls={it.photos} label={t('ie.photos')} />
            </div>
          )) : (
            <div className="card" style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13.5, color: 'var(--slate)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{live.description}</div>
              <MediaStrip urls={live.photos} label={t('ie.photos')} />
            </div>
          )}

          {/* Where the money is up to. Same states the card shows, with the
              numbers filled in — an approved estimate is itemised here rather
              than being a total they have to take on trust. */}
          {/* Paperwork on this job. Shown whenever there is any, in either
              portal — a homeowner's permit matters as much as a manager's
              inspection. Adding is offered to anyone who can see the job. */}
          {((live.documents || []).length > 0 || isManager) && (
            <div style={{ marginBottom: 18 }}>
              <DocumentList
                title={t('doc.documents')}
                documents={live.documents || []}
                onAdd={addRequestDocuments}
                emptyText={t('doc.noneJob')}
              />
            </div>
          )}

          {/* Over the spending limit. Louder than the ordinary estimate band,
              because this is the one state where work is BLOCKED waiting on
              them — and it says by how much, so the decision can be made off
              this screen without opening anything. */}
          {isManager && live.nteStatus === 'exceeded' && (
            <div className="alert alert-red" style={{ marginBottom: 16 }}>
              <strong>{t('acct.overLimitTitle')}</strong>
              <div style={{ marginTop: 4, lineHeight: 1.55 }}>
                {t('acct.overLimitBody')
                  .replace('{estimate}', fmtMoney(live.estimate?.amount))
                  .replace('{limit}', fmtMoney(live.nte?.amount))}
              </div>
              {account?.canApproveSpend ? (
                <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                  <button className="btn btn-green btn-sm" onClick={() => handleApproveEstimate(true)}>{t('acct.approveAnyway')}</button>
                  <button className="btn btn-outline btn-sm" onClick={() => handleApproveEstimate(false)}>{t('est.decline')}</button>
                </div>
              ) : (
                <div style={{ marginTop: 10, fontSize: 13 }}>{t('acct.needsManager')}</div>
              )}
            </div>
          )}

          {live.estimateStatus === 'pending' && !(isManager && live.nteStatus === 'exceeded') && (
            <div className="alert alert-amber" style={{ marginBottom: 16 }}>
              <strong>{t('ho.estimateReady')}</strong> {t('ho.tapReview')}
              <br />
              <button className="btn btn-amber btn-sm" style={{ marginTop: 8 }} onClick={() => goTo('screen-estimate-review')}>
                {t('ho.reviewEstimate')}
              </button>
            </div>
          )}

          {/* What the tech was told about getting in. Only ever present on a
              commercial work order, and worth showing back to the manager so a
              wrong gate code is spotted before the visit rather than after. */}
          {isManager && (live.access?.tenantName || live.access?.lockboxCode || live.access?.gateCode) && (
            <>
              <div className="sec-label">{t('acct.gettingIn')}</div>
              <div className="card" style={{ marginBottom: 16, fontSize: 13.5, lineHeight: 1.7 }}>
                {live.access.tenantName && <div>{t('acct.tenantName')}: {live.access.tenantName}{live.access.tenantPhone ? ` · ${live.access.tenantPhone}` : ''}</div>}
                {live.access.lockboxCode && <div>{t('acct.lockboxCode')}: {live.access.lockboxCode}</div>}
                {live.access.gateCode && <div>{t('acct.gateCode')}: {live.access.gateCode}</div>}
                {live.access.pets && <div>{t('acct.pets')}: {live.access.pets}</div>}
                {live.access.notes && <div>{live.access.notes}</div>}
              </div>
            </>
          )}
          {/* ── The work order ──
              An estimate is a price; a work order is the instruction to go and
              do it, and it is theirs to give. Offered only once the estimate is
              approved — the server holds the same order — and it carries no
              money at all: the estimate they approved is the price, and the
              sheet goes to a technician and a tenant as well as to them. */}
          {isManager && live.estimateStatus === 'approved' && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="sec-label" style={{ marginBottom: 6 }}>{t('ho.workOrder')}</div>
              {live.workOrder?.number ? (
                <>
                  <div style={{ fontSize: 15, color: 'var(--navy)', marginBottom: 8 }}>{live.workOrder.number}</div>
                  <button className="btn btn-outline btn-lg" onClick={() => setPrintDoc({ request: live, kind: 'work-order' })}>
                    {t('acct.viewWorkOrder')}
                  </button>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 13, color: 'var(--slate)', lineHeight: 1.5, marginBottom: 10 }}>{t('acct.workOrderHelp')}</div>
                  <button className="btn btn-primary btn-lg" disabled={isRaisingAccountWO} onClick={() => raiseAccountWorkOrder(live)}>
                    {t('acct.createWorkOrder')}
                  </button>
                </>
              )}
            </div>
          )}

          {/* Who is coming, and who they asked for. Both only ever on a
              commercial job: a homeowner never picks a subcontractor. */}
          {isManager && (live.technician || live.requestedVendorName) && (
            <div className="card" style={{ marginBottom: 16, fontSize: 13.5, lineHeight: 1.7 }}>
              {live.technician && (
                <div>
                  <span style={{ color: 'var(--muted)' }}>{t('acct.technician')}: </span>
                  {live.technician.name}
                  {live.technician.phone && <> · <a href={`tel:${live.technician.phone}`} style={{ color: 'var(--blue)', textDecoration: 'none' }}>{live.technician.phone}</a></>}
                </div>
              )}
              {live.requestedVendorName && !live.technician && (
                <div><span style={{ color: 'var(--muted)' }}>{t('acct.requestedTech')}: </span>{live.requestedVendorName}</div>
              )}
            </div>
          )}

          {live.estimateStatus === 'approved' && live.estimate && (
            <>
              <div className="sec-label">{t('est.yourEstimate')}</div>
              <div className="card" style={{ marginBottom: 16 }}>
                {live.estimate.breakdown?.map((row, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 14, gap: 12 }}>
                    <span>{row.label}</span>
                    <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtMoney(row.cost)}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0 0', fontSize: 16, fontWeight: 800, color: 'var(--navy)' }}>
                  <span>{t('est.total')}</span>
                  <span style={{ color: 'var(--green)' }}>{fmtMoney(live.estimate.amount)}</span>
                </div>
                {live.estimate.notes && (
                  <div style={{ marginTop: 12, padding: '10px 12px', background: 'var(--bg)', borderRadius: 'var(--radius-xs)', fontSize: 13, color: 'var(--slate)' }}>
                    <strong>{t('est.notes')}</strong> {live.estimate.notes}
                  </div>
                )}
                <div style={{ marginTop: 10, fontSize: 13, color: 'var(--green)', fontWeight: 600 }}>{t('ho.estApproved')} {fmtMoney(live.estimate.amount)}</div>
              </div>
            </>
          )}
          {live.invoiceStatus === 'pending' && (
            <div className="alert alert-amber" style={{ marginBottom: 16 }}>
              <strong>{t('ho.invoiceReceived')}</strong> {t('ho.invoiceBody').replace('{amount}', fmtMoney(live.invoice?.amount))}
              {invoiceOpen(live) && currentUser?.role === 'homeowner' && (
                <div>
                  <button className="btn btn-primary btn-sm" style={{ marginTop: 8 }}
                    onClick={() => goPay({ requestId: live._id, token: live.payToken, auth: authHeaders() })}>
                    Pay {fmtMoney(live.invoice?.amount)} online
                  </button>
                </div>
              )}
            </div>
          )}
          {live.status === 'paid' && (
            <div className="alert" style={{ marginBottom: 16, background: 'var(--green-light)', color: 'var(--green)' }}>{t('ho.jobComplete')}</div>
          )}

          {/* What the tech photographed on the way out. */}
          {(live.completionPhotos?.length > 0 || live.completionNote) && (
            <>
              <div className="sec-label">{t('ho.finishedWork')}</div>
              <div className="card" style={{ marginBottom: 16 }}>
                {live.completionNote && <div style={{ fontSize: 13.5, color: 'var(--slate)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{live.completionNote}</div>}
                <MediaStrip urls={live.completionPhotos} label={t('ie.photos')} />
                {live.completedAt && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 8 }}>{fmtDate(live.completedAt)}</div>}
              </div>
            </>
          )}

          {/* Talking about THIS job and nothing else. The thread is scoped to
              the work order on the server (Message.requestId), so a customer
              with three jobs on the go has three conversations and neither they
              nor Julian has to work out which one an answer belongs to. */}
          <div className="sec-label">{t('ho.aboutThisJob')}</div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, color: 'var(--slate)', lineHeight: 1.5, marginBottom: 12 }}>
              {t('ho.messagesHint')}
              {unread > 0 && <> <strong style={{ color: 'var(--blue)' }}>{t('ho.unreadHere').replace('{n}', unread)}</strong></>}
            </div>
            <MessageButton request={live} className="btn btn-primary btn-lg" />
          </div>

          {/* Their own copy of the estimate, to keep or to print. The same
              sheet Julian prints, minus the crew's side of it — the work order
              carries site instructions and who was assigned, which is ours. */}
          {live.estimate?.amount != null && (
            <button className="btn btn-outline btn-lg" style={{ marginBottom: 10 }} onClick={() => setPrintDoc({ request: live, kind: 'estimate' })}>
              {t('ho.printEstimate')}
            </button>
          )}

          <button className="btn btn-ghost btn-lg" style={{ marginTop: 4 }} onClick={() => goTo('screen-homeowner-dashboard')}>{t('nav.back')}</button>
        </div>
      )}
    </div>
  );
}

export function EstimateReview() {
  const { currentScreen, goTo, activeRequest, handleApproveEstimate } = useApp();
  const { t, optLabel } = useI18n();

  return (
    <div className={`screen ${currentScreen === 'screen-estimate-review' ? 'active' : ''}`}>
      <nav className="nav-public">
        <BrandMark />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ThemeToggle /><LanguageSwitcher />
          <button className="btn btn-ghost btn-sm" onClick={() => goTo('screen-homeowner-dashboard')}>{t('nav.back')}</button>
        </div>
      </nav>
      {activeRequest && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          <div className="sec-label">{t('est.yourEstimate')}</div>
          <div className="section-title" style={{ marginBottom: 4 }}>{optLabel(activeRequest.serviceType)}</div>
          <div className="qps-sheet-sub">{activeRequest.serviceAddress}</div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--blue)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 }}>{t('est.breakdown')}</div>
            {activeRequest.estimate?.breakdown?.map((row, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 14 }}>
                <span>{row.label}</span>
                <span style={{ fontWeight: 600 }}>{fmtMoney(row.cost)}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0 0', fontSize: 16, fontWeight: 800, color: 'var(--navy)' }}>
              <span>{t('est.total')}</span>
              <span style={{ color: 'var(--green)' }}>{fmtMoney(activeRequest.estimate?.amount)}</span>
            </div>
            {activeRequest.estimate?.notes && (
              <div style={{ marginTop: 12, padding: '10px 12px', background: 'var(--bg)', borderRadius: 'var(--radius-xs)', fontSize: 13, color: 'var(--slate)' }}>
                <strong>{t('est.notes')}</strong> {activeRequest.estimate.notes}
              </div>
            )}
            <div style={{ marginTop: 8, fontSize: 11, color: 'var(--muted)' }}>{t('ho.submitted')} {fmtDate(activeRequest.estimate?.submittedAt)}</div>
          </div>

          <div className="alert alert-blue" style={{ marginBottom: 16 }}>
            {t('est.noCharge')}
          </div>

          <button className="btn btn-green btn-lg" style={{ marginBottom: 10 }} onClick={() => handleApproveEstimate(true)}>
            {t('est.approve')}
          </button>
          <button className="btn btn-ghost btn-lg" onClick={() => handleApproveEstimate(false)}>
            {t('est.decline')}
          </button>
        </div>
      )}
    </div>
  );
}
